import { z } from "zod";
import { auditActorFields } from "../common/audit.js";
import { pagedResponse, paginationQuerySchema } from "../common/pagination.js";
const SESSION_STATUS = z.enum([
    "created",
    "queued",
    "in_progress",
    "processing",
    "completed",
    "partially_completed",
    "failed",
    "cancelled",
]);
const SESSION_TYPE = z.enum([
    "full_store_audit",
    "category_audit",
    "fixture_audit",
    "planogram_check",
    "oos_check",
    "price_check",
    "promo_check",
    "recapture",
    "demo",
    "other",
]);
const CAPTURE_SOURCE = z.enum(["mobile", "robot", "manual_upload", "api_import"]);
const CONTROL_ACTION = z.enum(["pause", "resume", "stop", "return_to_base"]);
export const captureSessionInfo = z.object({
    id: z.string().uuid(),
    /** Human scan name; null = never set → UI shows the auto label (store · id). */
    name: z.string().nullable(),
    storeId: z.string().uuid(),
    storeName: z.string().nullable(),
    deviceId: z.string().uuid().nullable(),
    deviceSerial: z.string().nullable(),
    captureSource: CAPTURE_SOURCE,
    sessionType: SESSION_TYPE,
    status: SESSION_STATUS,
    categoryIds: z.array(z.string().uuid()),
    controlAction: CONTROL_ACTION.nullable(),
    controlActionStatus: z.string().nullable(),
    failureReason: z.string().nullable(),
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
    taskCount: z.number().int(),
    metadata: z.record(z.unknown()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    ...auditActorFields,
});
export const createSessionRequest = z.object({
    /** Optional scan name (client pre-fills a default; user may edit). */
    name: z.string().trim().max(200).nullish(),
    storeId: z.string().uuid(),
    deviceId: z.string().uuid().nullish(),
    deviceSerial: z.string().min(1).max(128).nullish(),
    captureSource: CAPTURE_SOURCE,
    sessionType: SESSION_TYPE,
    categoryIds: z.array(z.string().uuid()).default([]),
    metadata: z.record(z.unknown()).default({}),
});
export const updateSessionRequest = z
    .object({
    name: z.string().trim().max(200).nullish(),
    status: SESSION_STATUS.optional(),
    startedAt: z.string().datetime().nullish(),
    finishedAt: z.string().datetime().nullish(),
    controlAction: CONTROL_ACTION.nullish(),
    failureReason: z.string().nullish(),
    metadata: z.record(z.unknown()).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field required" });
// Per-scan "email me the report when the scan finishes" opt-in. Stored on
// capture_sessions.metadata.reportEmail; written via PATCH .../report-settings.
export const reportEmailSettings = z.object({
    onComplete: z.boolean(),
    recipients: z.array(z.string().email()).max(20).optional(),
});
export const updateReportSettingsRequest = z.object({
    reportEmail: reportEmailSettings,
});
// Operator-triggered "send the scan report now". Optional recipient override;
// when omitted the worker falls back to the opt-in recipients / scan creator.
export const sendReportEmailRequest = z.object({
    recipient: z.string().email().optional(),
});
// Operator/console-initiated retry of a failed or partially-completed session.
// Optional `taskIds` retries a subset; default retries all retryable failed tasks.
export const retrySessionRequest = z
    .object({
    taskIds: z.array(z.string().uuid()).optional(),
})
    .default({});
export const retrySessionResponse = z.object({
    sessionId: z.string().uuid(),
    status: SESSION_STATUS,
    retriedTaskIds: z.array(z.string().uuid()),
});
/**
 * Inclusive YYYY-MM-DD date bounds on `started_at`. Powers deep-links
 * from the analytics trends chart ("show me the scans behind this
 * bucket") and is treated as a plain calendar-day filter — the server
 * casts `started_at::date` before comparing so it doesn't care about
 * UTC vs. tenant timezone drift.
 */
const dateOnly = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");
export const listSessionsQuery = paginationQuerySchema.extend({
    storeId: z.string().uuid().optional(),
    status: SESSION_STATUS.optional(),
    sessionType: SESSION_TYPE.optional(),
    captureSource: CAPTURE_SOURCE.optional(),
    startedFrom: dateOnly.optional(),
    startedTo: dateOnly.optional(),
    createdFrom: dateOnly.optional(),
    createdTo: dateOnly.optional(),
});
export const captureSessionsListResponse = pagedResponse(captureSessionInfo);
//# sourceMappingURL=capture-sessions.schemas.js.map